import React from 'react';
import Text from '../../shared/Text';
import FormValidationMessage from './form-validation-message';
import _debounce from 'lodash/debounce';
import {
  useLoadEmployeeInfo,
  useLoadTenantName,
  useSetSupportLoginData,
  useSubmitForm,
  useSupportLoginData,
} from './providers/supportLogin/hooks';
import {
  Autocomplete,
  AutocompleteValue,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { FormFieldWrapper, FormSubmitWrapper, FormWrapper } from './shared-form-components';
import { AsyncButton } from '@c42/component-library';

type TenantOption = {
  label: string;
  id: string;
};

const SupportLoginForm: React.FC = () => {
  const theme = useTheme();
  const loadTenantName = useLoadTenantName();
  const loadEmployeeInfo = useLoadEmployeeInfo();
  const submitForm = useSubmitForm();
  const setSupportLoginData = useSetSupportLoginData();
  const {
    accessType,
    employeeInfo,
    isEmployeeInfoError,
    isFetchingJwt,
    isFetchingTenantName,
    isJwtError,
    tenantId,
    tenantName,
  } = useSupportLoginData();

  const loadTenantNameDebounced = React.useCallback(
    _debounce((tenantId: string) => loadTenantName(tenantId), 200),
    [],
  );

  React.useEffect(() => {
    loadEmployeeInfo();
  }, []);

  React.useEffect(() => {
    if (tenantId) {
      loadTenantNameDebounced(tenantId);
    } else {
      setSupportLoginData((current) => ({
        ...current,
        tenantName: undefined,
      }));
    }
  }, [tenantId]);

  const submitFormHandler = (e?: React.FormEvent<any>) => {
    if (e && 'preventDefault' in e) {
      e.preventDefault();
    }
    submitForm();
  };

  const changeTenantIdHandler = (
    _e: React.FormEvent<any>,
    value: AutocompleteValue<TenantOption, false, any, any>,
  ) => {
    setSupportLoginData((current) => ({
      ...current,
      tenantId: typeof value === 'string' ? value : value?.id,
    }));
  };

  const changeAccessTypeHandler: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    setSupportLoginData((current) => ({
      ...current,
      accessType: event.target.value,
    }));
  };

  const isFormDisabled = !employeeInfo;

  let error: string;
  if (isEmployeeInfoError) {
    error = Text.get('support_login_employee_info_error');
  } else if (isJwtError) {
    error = Text.get('support_login_jwt_error');
  }

  const recentlyUsedTenants: TenantOption[] =
    employeeInfo?.recentTenants?.map((tenant) => ({
      label: tenant.tenantName,
      id: tenant.tenantId,
    })) || [];

  return (
    <FormWrapper>
      <form name="supportLoginForm" noValidate onSubmit={submitFormHandler}>
        <FormValidationMessage message={error} />

        <Typography variant="h5">{Text.get('support_login_email')}</Typography>

        <Box ml={2}>
          <Typography>{employeeInfo?.email}</Typography>
        </Box>

        <FormFieldWrapper>
          <Autocomplete
            disabled={isFormDisabled}
            forcePopupIcon={recentlyUsedTenants.length > 0}
            freeSolo
            fullWidth
            onChange={changeTenantIdHandler}
            onInputChange={changeTenantIdHandler}
            options={recentlyUsedTenants}
            value={tenantId || ''}
            renderInput={(params) => (
              <TextField {...params} autoComplete="off" label={Text.get('support_login_tenant_id_label')} />
            )}
          />
        </FormFieldWrapper>

        {(tenantName || isFetchingTenantName) && (
          <Box display="flex" gap={0.5} ml={2} mt={2}>
            <Typography whiteSpace="nowrap">
              <strong>{Text.get('support_login_tenant_name_label')}: </strong>
            </Typography>
            {tenantName && <Typography sx={{ wordBreak: 'break-word' }}>{tenantName}</Typography>}
            {!tenantName && isFetchingTenantName && (
              <Skeleton height={theme.typography.body1.lineHeight} variant="rectangular" width="100%" />
            )}
          </Box>
        )}

        <FormFieldWrapper>
          <FormControl fullWidth>
            <InputLabel id="access-type-label">{Text.get('support_login_access_type_label')}</InputLabel>
            <Select
              disabled={isFormDisabled}
              fullWidth
              label={Text.get('support_login_access_type_label')}
              labelId="access-type-label"
              onChange={changeAccessTypeHandler}
              value={accessType || ''}
            >
              {employeeInfo?.groups?.map((group) => (
                <MenuItem key={group} value={group}>
                  {group}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FormFieldWrapper>

        <FormSubmitWrapper>
          <AsyncButton
            data-test="submit"
            disabled={isFetchingJwt || isFormDisabled}
            isLoading={isFetchingJwt}
            type="submit"
            variant="contained"
          >
            {Text.get('login_button_sign_in')}
          </AsyncButton>
        </FormSubmitWrapper>
      </form>
    </FormWrapper>
  );
};

export default SupportLoginForm;
